import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Download, Mail } from "lucide-react";
import { Terminal } from "@/components/Terminal";
import { NetworkCanvas } from "@/components/NetworkCanvas";
import { scrollToSection } from "@/lib/scroll";

const MaskedLine = ({ children, delay, reduce }) => {
    if (reduce) return <span className="block">{children}</span>;
    return (
        <span className="block overflow-hidden pb-1">
            <motion.span
                className="block"
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
            >
                {children}
            </motion.span>
        </span>
    );
};

const FadeIn = ({ children, delay, reduce, className = "" }) => {
    if (reduce) return <div className={className}>{children}</div>;
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
            className={className}
        >
            {children}
        </motion.div>
    );
};

export const Hero = () => {
    const reduce = useReducedMotion();
    const { scrollY } = useScroll();
    const terminalY = useTransform(scrollY, [0, 700], [0, reduce ? 0 : 90]);
    const bgY = useTransform(scrollY, [0, 700], [0, reduce ? 0 : -60]);

    return (
        <section
            id="home"
            data-testid="hero-section"
            className="bg-grid relative flex min-h-screen items-center overflow-hidden pt-[72px]"
        >
            <motion.div style={{ y: bgY }} className="absolute inset-0" aria-hidden="true">
                <NetworkCanvas className="h-full w-full opacity-70" />
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,#050505_78%)]" />
                <div className="absolute -left-40 top-1/4 h-96 w-96 rounded-full bg-neon/[0.04] blur-[120px]" />
                <div className="absolute -right-40 bottom-1/4 h-96 w-96 rounded-full bg-cyber/[0.05] blur-[120px]" />
            </motion.div>

            <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-14 px-5 py-24 sm:px-8 lg:grid-cols-12 lg:gap-10">
                <div className="lg:col-span-7">
                    <FadeIn delay={0.15} reduce={reduce}>
                        <p className="mb-8 inline-flex items-center gap-2 border border-white/10 bg-white/[0.02] px-4 py-2 font-mono2 text-xs text-neon sm:text-sm">
                            <span className="text-zinc-500">&gt;</span> cybersecurity.portfolio
                        </p>
                    </FadeIn>

                    <h1
                        data-testid="hero-name"
                        className="font-display text-5xl font-black leading-[0.95] tracking-tighter text-zinc-100 sm:text-7xl xl:text-8xl"
                    >
                        <MaskedLine delay={0.3} reduce={reduce}>HARSHDEEP</MaskedLine>
                        <MaskedLine delay={0.45} reduce={reduce}>
                            <span className="text-outline">SINGH</span>
                        </MaskedLine>
                    </h1>

                    <div className="mt-8 font-display text-xl font-bold tracking-tight sm:text-2xl">
                        <MaskedLine delay={0.65} reduce={reduce}>
                            <span className="text-zinc-200">Cybersecurity Enthusiast</span>
                        </MaskedLine>
                        <MaskedLine delay={0.8} reduce={reduce}>
                            <span className="text-neon">Aspiring Penetration Tester</span>
                        </MaskedLine>
                    </div>

                    <FadeIn delay={1} reduce={reduce}>
                        <p className="mt-7 max-w-xl text-base leading-relaxed text-zinc-400 md:text-lg">
                            Passionate about identifying vulnerabilities, analyzing security
                            weaknesses, and developing practical cybersecurity skills through
                            hands-on labs and technical problem solving.
                        </p>
                    </FadeIn>

                    <FadeIn delay={1.15} reduce={reduce}>
                        <div className="mt-10 flex flex-wrap items-center gap-4">
                            <button
                                type="button"
                                data-testid="hero-view-projects-btn"
                                onClick={() => scrollToSection("projects")}
                                className="group flex items-center gap-2 bg-neon px-6 py-3.5 font-mono2 text-sm font-semibold text-black transition-colors duration-300 hover:bg-cyber"
                            >
                                View Projects
                                <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                            </button>
                            <button
                                type="button"
                                data-testid="hero-contact-btn"
                                onClick={() => scrollToSection("contact")}
                                className="flex items-center gap-2 border border-white/15 px-6 py-3.5 font-mono2 text-sm text-zinc-200 transition-colors duration-300 hover:border-neon/60 hover:text-neon"
                            >
                                <Mail size={15} strokeWidth={1.5} />
                                Contact Me
                            </button>
                            <a
                                href="/resume.pdf"
                                download
                                data-testid="hero-download-resume-btn"
                                className="flex items-center gap-2 border border-neon/60 px-6 py-3.5 font-mono2 text-sm text-neon transition-colors duration-300 hover:bg-neon hover:text-black"
                            >
                                <Download size={15} strokeWidth={1.5} />
                                Resume
                            </a>
                        </div>
                    </FadeIn>

                    <FadeIn delay={1.3} reduce={reduce}>
                        <p
                            data-testid="hero-status-indicator"
                            className="mt-10 flex items-center gap-3 font-mono2 text-xs text-zinc-400 sm:text-sm"
                        >
                            <span className="relative flex h-2.5 w-2.5">
                                <span className="absolute inline-flex h-full w-full animate-pulse-dot rounded-full bg-neon" />
                                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-neon" />
                            </span>
                            Open to Cybersecurity Opportunities
                        </p>
                    </FadeIn>
                </div>

                <motion.div style={{ y: terminalY }} className="lg:col-span-5">
                    <FadeIn delay={0.9} reduce={reduce}>
                        <Terminal />
                        <div className="mt-4 flex items-center justify-between font-mono2 text-[11px] text-zinc-600">
                            <span>uptime: continuous_learning</span>
                            <span className="text-neon/70">session: active</span>
                        </div>
                    </FadeIn>
                </motion.div>
            </div>
        </section>
    );
};
